import type { FocusEventHandler, PointerEventHandler } from 'react';
import {
  FilterRailColorOption,
  FilterRailColorOptionCount,
  FilterRailColorOptionLabel,
  FilterRailColorOptionText,
} from '@vs-shop/commerce-ui/filterRail';
import { CheckboxSwatch } from '@vs-shop/ui/checkbox';

import type { ColorCode } from '../../../config/colorsForProductFilters';
import { colorCodeMap } from '../../../config/colorsForProductFilters';

/**
 * The colour codes whose swatch is too pale to carry a light tick. rcprofi's
 * own vocabulary, resolved here rather than passed on: the shared swatch is
 * told how light it reads, never what any catalogue calls its colours.
 */
const paleColorCodes: readonly ColorCode[] = ['bila', 'cira'];

type Props = {
  // oxlint-disable-next-line vspoint-effect/branded-component-props -- presentational facet cell key, not a single entity id
  id: string;
  // Colour code key (e.g. `cervena`), resolved to a CSS colour here.
  colorCode: ColorCode;
  label: string;
  // Optional product count: rendered under the swatch (search rail) and folded
  // into the title attribute. Absent on the category rail.
  count?: number | undefined;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: () => void;
  onPointerEnter?: PointerEventHandler<HTMLLabelElement>;
  onPointerLeave?: PointerEventHandler<HTMLLabelElement>;
  onFocus?: FocusEventHandler<HTMLLabelElement>;
  onBlur?: FocusEventHandler<HTMLLabelElement>;
};

/**
 * rcprofi's arrangement of one colour swatch in the colour filter dimension,
 * shared by the category and search rails. Purely presentational — the
 * dimension owns the atom subscription. When a `count` is supplied (search) it
 * renders under the swatch; the category rail omits it and shows only the
 * swatch and its screen-reader name.
 *
 * The anatomy is `@vs-shop/commerce-ui`'s. What stays here is rcprofi's colour
 * vocabulary: the code → CSS colour lookup, and which codes read as pale.
 */
export function FilterColorCell({
  id,
  colorCode,
  label,
  count,
  checked,
  disabled = false,
  onCheckedChange,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
}: Props) {
  const title = typeof count === 'number' ? `${label} (${String(count)})` : label;

  return (
    <FilterRailColorOption>
      <FilterRailColorOptionLabel
        htmlFor={id}
        title={title}
        data-disabled={disabled || undefined}
        className={disabled ? 'opacity-50' : undefined}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <CheckboxSwatch
          aria-label={label}
          id={id}
          color={colorCodeMap[colorCode]}
          tone={paleColorCodes.includes(colorCode) ? 'light' : 'dark'}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
        />
        {typeof count === 'number' ? (
          <FilterRailColorOptionCount>{count}</FilterRailColorOptionCount>
        ) : null}
        <FilterRailColorOptionText>{label}</FilterRailColorOptionText>
      </FilterRailColorOptionLabel>
    </FilterRailColorOption>
  );
}
